/**
 * Backlog #14: polls (one question) and surveys (several questions) posted into a conversation.
 * Validation and tallying are shared by the API, the web client and tests.
 */
export const POLL_QUESTION_KINDS = ['single', 'multi', 'rating', 'text'] as const;
export type PollQuestionKind = (typeof POLL_QUESTION_KINDS)[number];
export const POLL_QUESTION_LABELS: Record<PollQuestionKind, string> = {
  single: 'Single choice', multi: 'Multiple choice', rating: 'Rating / scale', text: 'Free text',
};
export type PollKind = 'poll' | 'survey';
export type PollResultsVisibility = 'live' | 'after_close';
export type PollPolicy = 'members' | 'moderators';

export const POLL_LIMITS = {
  title: 200, description: 1000, prompt: 300, option: 120, options: 20, questions: 20, text: 2000, scaleMax: 10,
} as const;

export interface PollQuestion {
  id: string;
  prompt: string;
  kind: PollQuestionKind;
  required: boolean;
  /** single / multi */
  options: string[];
  /** multi: optional cap on how many may be picked */
  max_choices?: number | null;
  /** rating */
  scale_min?: number;
  scale_max?: number;
  scale_labels?: [string, string] | null;
}

export interface PollInput {
  kind: PollKind;
  title: string;
  description?: string | null;
  anonymous: boolean;
  results_visibility: PollResultsVisibility;
  allow_change: boolean;
  /** ISO timestamp, or null for no close date */
  closes_at?: string | null;
  /** a quick poll's question may omit prompt (the title is used) */
  questions: Array<Omit<PollQuestion, 'id' | 'required' | 'options' | 'prompt'> & { id?: string; prompt?: string; required?: boolean; options?: string[] }>;
}

/** single: option index; multi: option indexes; rating: number; text: string */
export type PollAnswer = number | number[] | string;
export type PollAnswers = Record<string, PollAnswer>;

export interface PollVoter { user_id: string; display_name: string }
export interface PollQuestionResult {
  question_id: string;
  kind: PollQuestionKind;
  answered: number;
  /** single / multi (and rating: one bucket per scale point, label = the number) */
  options: Array<{ label: string; count: number; voters?: PollVoter[] }>;
  average?: number | null;
  /** text answers; names only for named polls */
  texts?: Array<{ text: string; user_id?: string; display_name?: string }>;
}

export interface PollView {
  id: string;
  message_id: string;
  conversation_id: string;
  kind: PollKind;
  title: string;
  description: string | null;
  anonymous: boolean;
  results_visibility: PollResultsVisibility;
  allow_change: boolean;
  closes_at: string | null;
  closed_at: string | null;
  is_closed: boolean;
  created_by: string;
  created_by_name: string;
  created_at: string;
  questions: PollQuestion[];
  response_count: number;
  /** current conversation members who can respond */
  eligible_count: number;
  my_answers: PollAnswers | null;
  my_responded_at: string | null;
  can_respond: boolean;
  /** why the user cannot respond (closed, already answered, not a member) */
  respond_blocked_reason: string | null;
  can_see_results: boolean;
  /** creator / channel moderators: close, remind, export */
  can_manage: boolean;
  last_reminder_at: string | null;
  results: PollQuestionResult[] | null;
  /** named polls, managers only: members who have not answered yet */
  non_respondents?: PollVoter[];
  respondents?: Array<PollVoter & { responded_at: string }>;
}

const clean = (s: unknown) => (typeof s === 'string' ? s.trim() : '');

/** Validates and normalises the poll definition. Returns errors keyed by path ("title", "questions.0.options"). */
export function validatePollInput(input: PollInput, now = new Date()): { poll: PollInput & { questions: PollQuestion[] }; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const title = clean(input.title);
  if (!title) errors.title = 'Add a question or title';
  else if (title.length > POLL_LIMITS.title) errors.title = `Keep the title under ${POLL_LIMITS.title} characters`;
  const description = clean(input.description) || null;
  if (description && description.length > POLL_LIMITS.description) errors.description = 'Description is too long';
  const kind: PollKind = input.kind === 'survey' ? 'survey' : 'poll';
  const raw = Array.isArray(input.questions) ? input.questions : [];
  if (raw.length === 0) errors.questions = 'Add at least one question';
  if (raw.length > POLL_LIMITS.questions) errors.questions = `At most ${POLL_LIMITS.questions} questions`;
  if (kind === 'poll' && raw.length > 1) errors.questions = 'A quick poll has one question - use a survey for more';
  const seen = new Set<string>();
  const questions: PollQuestion[] = raw.slice(0, POLL_LIMITS.questions).map((q, i) => {
    const k: PollQuestionKind = (POLL_QUESTION_KINDS as readonly string[]).includes(q?.kind) ? q.kind : 'single';
    if (!(POLL_QUESTION_KINDS as readonly string[]).includes(q?.kind)) errors[`questions.${i}.kind`] = 'Unknown question type';
    // A quick poll's question is its title.
    const prompt = clean(q?.prompt) || (kind === 'poll' ? title : '');
    if (!prompt) errors[`questions.${i}.prompt`] = 'Add the question';
    else if (prompt.length > POLL_LIMITS.prompt) errors[`questions.${i}.prompt`] = 'Question is too long';
    let id = clean(q?.id).replace(/[^a-z0-9_-]/gi, '').slice(0, 40) || `q${i + 1}`;
    while (seen.has(id)) id = `${id}_${i + 1}`;
    seen.add(id);
    const out: PollQuestion = { id, prompt, kind: k, required: q?.required !== false, options: [] };
    if (k === 'single' || k === 'multi') {
      const opts = (Array.isArray(q?.options) ? q.options : []).map(clean).filter(Boolean);
      const lower = opts.map(o => o.toLowerCase());
      if (opts.length < 2) errors[`questions.${i}.options`] = 'Add at least two options';
      else if (opts.length > POLL_LIMITS.options) errors[`questions.${i}.options`] = `At most ${POLL_LIMITS.options} options`;
      else if (new Set(lower).size !== lower.length) errors[`questions.${i}.options`] = 'Options must be different';
      else if (opts.some(o => o.length > POLL_LIMITS.option)) errors[`questions.${i}.options`] = 'An option is too long';
      out.options = opts.slice(0, POLL_LIMITS.options);
      if (k === 'multi' && q?.max_choices != null) {
        const m = Number(q.max_choices);
        if (!Number.isInteger(m) || m < 1 || m > out.options.length) errors[`questions.${i}.max_choices`] = 'Pick limit must be between 1 and the number of options';
        else out.max_choices = m;
      }
    } else if (k === 'rating') {
      const min = q?.scale_min == null ? 1 : Number(q.scale_min);
      const max = q?.scale_max == null ? 5 : Number(q.scale_max);
      if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || min > 1 || max < 2 || max > POLL_LIMITS.scaleMax) {
        errors[`questions.${i}.scale`] = `Scale runs from 0 or 1 up to 2-${POLL_LIMITS.scaleMax}`;
      }
      out.scale_min = min; out.scale_max = max;
      const labels = Array.isArray(q?.scale_labels) ? q.scale_labels.map(clean) : [];
      out.scale_labels = labels.some(Boolean) ? [labels[0] ?? '', labels[1] ?? ''] : null;
      if (out.scale_labels?.some(l => l.length > 40)) errors[`questions.${i}.scale`] = 'Scale labels are too long';
    }
    return out;
  });
  let closes_at: string | null = null;
  if (input.closes_at) {
    const t = Date.parse(input.closes_at);
    if (Number.isNaN(t)) errors.closes_at = 'Invalid close date';
    else if (t <= now.getTime() + 60_000) errors.closes_at = 'Close date must be in the future';
    else if (t > now.getTime() + 366 * 86400_000) errors.closes_at = 'Close date must be within a year';
    else closes_at = new Date(t).toISOString();
  }
  // after_close without a close date is allowed: results appear when the creator closes it.
  return {
    poll: {
      kind, title, description, anonymous: Boolean(input.anonymous),
      results_visibility: input.results_visibility === 'after_close' ? 'after_close' : 'live',
      allow_change: input.allow_change !== false, closes_at, questions,
    },
    errors,
  };
}

/** Validates one person's answers against the questions. Unanswered optional questions are dropped. */
export function validatePollAnswers(questions: PollQuestion[], raw: Record<string, unknown>): { answers: PollAnswers; errors: Record<string, string> } {
  const answers: PollAnswers = {};
  const errors: Record<string, string> = {};
  const src = raw && typeof raw === 'object' ? raw : {};
  for (const key of Object.keys(src)) if (!questions.some(q => q.id === key)) errors[key] = 'Unknown question';
  for (const q of questions) {
    const v = src[q.id];
    const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    if (empty) { if (q.required) errors[q.id] = 'Please answer this question'; continue; }
    if (q.kind === 'single') {
      const n = Number(v);
      if (!Number.isInteger(n) || n < 0 || n >= q.options.length) errors[q.id] = 'Pick one of the options';
      else answers[q.id] = n;
    } else if (q.kind === 'multi') {
      const arr = Array.isArray(v) ? v.map(Number) : [Number(v)];
      if (arr.some(n => !Number.isInteger(n) || n < 0 || n >= q.options.length)) errors[q.id] = 'Pick from the options';
      else {
        const uniq = [...new Set(arr)].sort((a, b) => a - b);
        if (q.max_choices && uniq.length > q.max_choices) errors[q.id] = `Pick up to ${q.max_choices}`;
        else answers[q.id] = uniq;
      }
    } else if (q.kind === 'rating') {
      const n = Number(v);
      if (!Number.isInteger(n) || n < (q.scale_min ?? 1) || n > (q.scale_max ?? 5)) errors[q.id] = `Choose ${q.scale_min ?? 1}-${q.scale_max ?? 5}`;
      else answers[q.id] = n;
    } else {
      const s = typeof v === 'string' ? v.trim() : '';
      if (!s) { if (q.required) errors[q.id] = 'Please answer this question'; continue; }
      if (s.length > POLL_LIMITS.text) errors[q.id] = `Keep it under ${POLL_LIMITS.text} characters`;
      else answers[q.id] = s;
    }
  }
  return { answers, errors };
}

export interface PollResponseRow { user_id: string; display_name: string; answers: PollAnswers; responded_at: string }

/** Aggregates responses. With anonymous=true no voter identities are included and text answers are sorted, not in response order. */
export function tallyPoll(questions: PollQuestion[], responses: PollResponseRow[], anonymous: boolean): PollQuestionResult[] {
  return questions.map(q => {
    const given = responses.filter(r => r.answers[q.id] !== undefined);
    const res: PollQuestionResult = { question_id: q.id, kind: q.kind, answered: given.length, options: [] };
    if (q.kind === 'single' || q.kind === 'multi') {
      res.options = q.options.map((label, i) => {
        const who = given.filter(r => { const a = r.answers[q.id]; return Array.isArray(a) ? a.includes(i) : a === i; });
        return { label, count: who.length, ...(anonymous ? {} : { voters: who.map(r => ({ user_id: r.user_id, display_name: r.display_name })) }) };
      });
    } else if (q.kind === 'rating') {
      const min = q.scale_min ?? 1; const max = q.scale_max ?? 5;
      const nums = given.map(r => Number(r.answers[q.id]));
      for (let p = min; p <= max; p++) {
        const who = given.filter(r => Number(r.answers[q.id]) === p);
        res.options.push({ label: String(p), count: who.length, ...(anonymous ? {} : { voters: who.map(r => ({ user_id: r.user_id, display_name: r.display_name })) }) });
      }
      res.average = nums.length ? Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10 : null;
    } else {
      const texts = given.map(r => ({ text: String(r.answers[q.id]), user_id: r.user_id, display_name: r.display_name, at: r.responded_at }));
      res.texts = anonymous
        ? texts.map(t => ({ text: t.text })).sort((a, b) => a.text.localeCompare(b.text))
        : texts.sort((a, b) => a.at.localeCompare(b.at)).map(({ at: _at, ...t }) => t);
    }
    return res;
  });
}

/** Human-readable answer for CSV / text. */
export function formatPollAnswer(q: PollQuestion, a: PollAnswer | undefined): string {
  if (a === undefined || a === null) return '';
  if (q.kind === 'single') return q.options[Number(a)] ?? '';
  if (q.kind === 'multi') return (Array.isArray(a) ? a : [a]).map(i => q.options[Number(i)]).filter(Boolean).join('; ');
  return String(a);
}

/** Plain-text body of the poll message: what search, the assistant, notifications and older clients see. */
export function renderPollText(p: { kind: PollKind; title: string; description?: string | null; questions: PollQuestion[] }): string {
  const lines = [`📊 ${p.kind === 'survey' ? 'Survey' : 'Poll'}: ${p.title}`];
  if (p.description) lines.push(p.description);
  const many = p.questions.length > 1 || p.kind === 'survey';
  p.questions.forEach((q, i) => {
    if (many) lines.push(`${i + 1}. ${q.prompt}`);
    if (q.kind === 'single' || q.kind === 'multi') q.options.forEach(o => lines.push(`• ${o}`));
    else if (q.kind === 'rating') lines.push(`(rating ${q.scale_min ?? 1}-${q.scale_max ?? 5})`);
    else if (!many) lines.push('(free text)');
  });
  return lines.join('\n');
}

/**
 * Composer shortcut: "/poll Where shall we go? | Pub | Bowling | Pizza".
 * Returns null when the text is not a /poll command; otherwise the parsed question and options (possibly empty).
 */
export function parsePollCommand(text: string): { question: string; options: string[] } | null {
  const m = /^\/poll(?:\s+([\s\S]*))?$/i.exec(text.trim());
  if (!m) return null;
  const parts = (m[1] ?? '').split(/\s*\|\s*|\n+/).map(s => s.trim()).filter(Boolean);
  return { question: parts[0] ?? '', options: parts.slice(1) };
}
